<template>
  <div>
    <div v-if="(containerEvents || []).length === 0" class="uk-text-muted uk-text-small cte-empty">
      No terminal events yet.
    </div>

    <div v-else class="cte">
      <table class="uk-table uk-table-small uk-table-striped uk-table-divider uk-table-hover cte-table">
        <thead>
          <tr>
            <th></th>
            <th></th>
            <th></th>
            <th colspan="5" style="text-align: center; border-bottom: 3px double black; padding: 0;">Holds</th>
            <th></th>
            <th></th>
          </tr>
          <tr>
            <th class="cte-date-col ">Date</th>
            <th class="cte-condensed-col">Available?</th>
            <th class="cte-lcation-col">Location</th>
            <th class="cte-condensed-col">Customs</th>
            <th class="cte-condensed-col">Shipline</th>
            <th class="cte-condensed-col">Terminal</th>
            <th class="cte-condensed-col">USDA</th>
            <th class="cte-condensed-col">Other</th>
            <th class="cte-condensed-col">Demurrage (Fees)</th>
            <th class="">Last Free Day</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, idx) in displayRows" :key="idx">
            <ContainerTerminalEventsTableRowV2 v-if="getIsV2Row(row)" :row="row" :previous-row="displayRows?.[idx + 1]" :terminal="terminal"/>
            <ContainerTerminalEventsTableRowV1 v-else :row="row" :previous-row="getPreviousV1Row(idx)" :terminal="terminal" />
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import ContainerTerminalEventsTableRowV1 from './ContainerTerminalEventsTableRowV1.vue';
import ContainerTerminalEventsTableRowV2 from './ContainerTerminalEventsTableRowV2.vue';
import { defineComponent } from 'vue';
import sort from 'fast-sort';

export default defineComponent({
  components: {
    ContainerTerminalEventsTableRowV1,
    ContainerTerminalEventsTableRowV2,
  },
  props: {
    containerEvents: {
      type: Array,
      required: true,
    },
    terminal: {
      type: String,
      required: true,
    }
  },
  methods: {
    getIsV2Row(rowOrEvent: any): boolean {
      return String(rowOrEvent?.version) === '2';
    },
    getPreviousV1Row(idx: number): any {
      // The adjacent older display row, only when it is also a v1 row.
      const prev = (this as any).displayRows?.[idx + 1];
      if (!prev || this.getIsV2Row(prev)) return null;
      return prev;
    },
    normalizeValue(value: any): string {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    },
    buildV1DisplayRows(v1ContainerEvents: any[]): any[] {
      // Collapse consecutive v1 events whose displayed columns match into one
      // row that carries the hidden-diff summary for its tooltip.
      const vm = this as any;
      const events = v1ContainerEvents.map((containerEvent: any) => JSON.parse(containerEvent.event));
      const dates = v1ContainerEvents.map((containerEvent: any) => containerEvent.date);
      if (events.length === 0) return [];

      const displayFields = [
        'yard_status',
        'customs',
        'line',
        'terminal',
        'demurrage_hold',
        'demurrage_lfd',
        'demurrage_amount',
      ];
      const normalize = (val: any) => vm.normalizeValue(val);
      const sameForDisplay = (a: any, b: any) =>
        displayFields.every((f) => normalize(a?.[f]) === normalize(b?.[f]));

      // Build groups of consecutive events that match for displayed columns.
      const groups: Array<{ idxs: number[] }> = [];
      for (let i = 0; i < events.length; i += 1) {
        const current = events[i];
        const lastGroup = groups[groups.length - 1];
        if (!lastGroup) {
          groups.push({ idxs: [i] });
          continue;
        }

        const lastIdx = lastGroup.idxs[lastGroup.idxs.length - 1];
        const lastEvent = events[lastIdx];

        if (sameForDisplay(current, lastEvent)) {
          lastGroup.idxs.push(i);
        } else {
          groups.push({ idxs: [i] });
        }
      }

      const diffForGroup = (idxs: number[]) => {
        if (idxs.length <= 1) return {};

        // Collect any non-displayed key whose value varies within the collapsed group.
        const keys = new Set<string>();
        idxs.forEach((i) => Object.keys(events[i] || {}).forEach((k) => keys.add(k)));
        displayFields.forEach((k) => keys.delete(k));

        const diffs: Record<string, { from: any; to: any }> = {};

        keys.forEach((k) => {
          const normalizedValues = idxs.map((i) => normalize(events[i]?.[k]));
          const unique = Array.from(new Set(normalizedValues));
          if (unique.length > 1) {
            // Use oldest->newest for a simple summary, while still detecting intermediate changes.
            const oldestIdx = idxs[idxs.length - 1];
            const newestIdx = idxs[0];
            diffs[k] = { from: events[oldestIdx]?.[k], to: events[newestIdx]?.[k] };
          }
        });

        return diffs;
      };

      return groups.map((g) => {
        const idx0 = g.idxs[0];
        return {
          version: '1',
          event: events[idx0],
          date: dates[idx0] ?? '',
          collapsedCount: g.idxs.length - 1,
          collapseDiffs: diffForGroup(g.idxs),
        };
      });
    },
  },
  computed: {
    sortedContainerEvents(): any[] {
      const events = (this as any).containerEvents || [];

      return sort([...events]).desc((u: any) => u.id);
    },

    displayRows(): any[] {
      // Newest-first mix of row shapes: v2 container events pass through as-is
      // (their row component reads parsed/raw itself); runs of consecutive v1
      // events go through the collapse/grouping pipeline.
      const vm = this as any;
      const rows: any[] = [];
      let v1Batch: any[] = [];

      const flushV1Batch = () => {
        if (v1Batch.length === 0) return;
        rows.push(...vm.buildV1DisplayRows(v1Batch));
        v1Batch = [];
      };

      (vm.sortedContainerEvents || []).forEach((containerEvent: any) => {
        if (vm.getIsV2Row(containerEvent)) {
          flushV1Batch();
          rows.push(containerEvent);
        } else {
          v1Batch.push(containerEvent);
        }
      });
      flushV1Batch();

      return rows;
    },
  },
});
</script>

<style scoped>
 .cte-table {
  table-layout: auto;
 }

 .cte-table :deep(th) {
    text-transform: none;
   
 }

 .cte-empty {
  padding: 8px 0;
 }

 .cte-date-col {
  width: 40px;
  white-space: nowrap;
 }
 .cte-location-col {
  width: 180px;
 }
 .cte-condensed-col {
  width: 10px;
  white-space: normal;
}

.cte {
  margin-top: 10px;
  border-top: 1px dashed #cbd5e1;
}
</style>
